import React, { useState, useEffect } from 'react';
import styles from './WerkplanningModule.module.css';
import { PlanningItem, PlanningType } from './types';
import { Modal } from '../common/Modal/Modal';

interface EditItemModalProps {
  item: PlanningItem;
  onClose: () => void;
  onSave: (item: PlanningItem) => void;
  onDelete?: (id: string) => void;
}

export const EditItemModal: React.FC<EditItemModalProps> = ({ item, onClose, onSave, onDelete }) => {
  const [formData, setFormData] = useState<PlanningItem>(item);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setFormData(item);
  }, [item]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title?.trim()) {
      newErrors.title = 'Titel is verplicht';
    }

    if (!formData.date) {
      newErrors.date = 'Datum is verplicht';
    }

    if (!formData.responsible?.trim()) {
      newErrors.responsible = 'Verantwoordelijke is verplicht';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validate()) {
      onSave(formData);
    }
  };

  const handleDelete = () => {
    if (window.confirm('Weet u zeker dat u dit item wilt verwijderen?')) {
      if (onDelete) {
        onDelete(item.id);
        onClose();
      }
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Item bewerken">
      <form onSubmit={handleSubmit} className={styles.modalForm}>
        <div className={styles.formSection}>
          <h3 className={styles.sectionTitle}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            Algemene informatie
          </h3>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Type
                <span className={styles.required}>*</span>
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className={styles.select}
              >
                <option value="taak">Taak</option>
                <option value="mijlpaal">Mijlpaal</option>
                <option value="communicatie">Communicatie</option>
                <option value="bijeenkomst">Bijeenkomst</option>
                <option value="deadline">Deadline</option>
                <option value="vergadering">Vergadering</option>
                <option value="presentatie">Presentatie</option>
                <option value="rapport">Rapport</option>
                <option value="overig">Overig</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                Status
                <span className={styles.required}>*</span>
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className={styles.select}
              >
                <option value="Gepland">Gepland</option>
                <option value="In uitvoering">In uitvoering</option>
                <option value="Voltooid">Voltooid</option>
                <option value="Geannuleerd">Geannuleerd</option>
                <option value="Verlopen">Verlopen</option>
              </select>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Titel
              <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={`${styles.input} ${errors.title ? styles.error : ''}`}
              placeholder="Bijv. Bewonersbijeenkomst fase 2"
            />
            {errors.title && <span className={styles.errorText}>{errors.title}</span>}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Omschrijving
            </label>
            <textarea
              name="description"
              value={formData.description || ''}
              onChange={handleChange}
              className={styles.textarea}
              rows={3}
              placeholder="Optionele beschrijving..."
            />
          </div>
        </div>

        <div className={styles.formSection}>
          <h3 className={styles.sectionTitle}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            Planning
          </h3>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Datum
                <span className={styles.required}>*</span>
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className={`${styles.input} ${errors.date ? styles.error : ''}`}
              />
              {errors.date && <span className={styles.errorText}>{errors.date}</span>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                Tijd
              </label>
              <input
                type="time"
                name="time"
                value={formData.time || ''}
                onChange={handleChange}
                className={styles.input}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Prioriteit
              </label>
              <select
                name="priority"
                value={(formData as any).priority || 'medium'}
                onChange={handleChange}
                className={styles.select}
              >
                <option value="low">Laag</option>
                <option value="medium">Gemiddeld</option>
                <option value="high">Hoog</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                Locatie
              </label>
              <input
                type="text"
                name="location"
                value={formData.location || ''}
                onChange={handleChange}
                className={styles.input}
                placeholder="Bijv. Gemeentehuis"
              />
            </div>
          </div>
        </div>

        <div className={styles.formSection}>
          <h3 className={styles.sectionTitle}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            Betrokkenen
          </h3>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Verantwoordelijke
                <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                name="responsible"
                value={formData.responsible}
                onChange={handleChange}
                className={`${styles.input} ${errors.responsible ? styles.error : ''}`}
                placeholder="Naam of functie"
              />
              {errors.responsible && <span className={styles.errorText}>{errors.responsible}</span>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                Doelgroep
              </label>
              <input
                type="text"
                name="targetAudience"
                value={formData.targetAudience || ''}
                onChange={handleChange}
                className={styles.input}
                placeholder="Bijv. Omwonenden, Klankbordgroep"
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Communicatiekanaal
            </label>
            <select
              name="channel"
              value={formData.channel || ''}
              onChange={handleChange}
              className={styles.select}
            >
              <option value="">Selecteer een kanaal...</option>
              <option value="Email">Email</option>
              <option value="Brief">Brief</option>
              <option value="Website">Website</option>
              <option value="Social Media">Social Media</option>
              <option value="Fysieke bijeenkomst">Fysieke bijeenkomst</option>
              <option value="Online bijeenkomst">Online bijeenkomst</option>
              <option value="Telefoon">Telefoon</option>
              <option value="Anders">Anders</option>
            </select>
          </div>
        </div>

        <div className={styles.modalActions}>
          <div className={styles.modalActionsLeft}>
            {onDelete && (
              <button type="button" onClick={handleDelete} className={styles.btnDanger}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
                Verwijderen
              </button>
            )}
          </div>
          <div className={styles.modalActionsRight}>
            <button type="button" onClick={onClose} className={styles.btnSecondary}>
              Annuleren
            </button>
            <button type="submit" className={styles.btnPrimary}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                <polyline points="17 21 17 13 7 13 7 21" />
                <polyline points="7 3 7 8 15 8" />
              </svg>
              Opslaan
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};